import React, { useState, memo, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar: React.FC = memo(() => {
  const location = useLocation();
  const { isAuthenticated, user, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };
  
  // Close the user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-green-400 shadow-sm fixed top-0 left-0 right-0 z-50">
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo and Brand */}
        <div className="flex items-center">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <h1 className="text-lg sm:text-xl font-bold">🎵 Bandcamp Explorer</h1>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        {isAuthenticated && (
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-green-300 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        )}

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-between flex-1 ml-6">
          {isAuthenticated && (
            <div className="flex gap-4">
              <Link 
                to="/" 
                className={`text-gray-700 hover:text-gray-900 transition-colors font-bold ${
                  location.pathname === '/' ? 'text-gray-900 underline underline-offset-4' : 'text-gray-600'
                }`}
              >
                New
              </Link>
              <Link 
                to="/collection" 
                className={`text-gray-700 hover:text-gray-900 transition-colors font-bold ${
                  location.pathname === '/collection' ? 'text-gray-900 underline underline-offset-4' : 'text-gray-600'
                }`}
              >
                Collection
              </Link>
              <Link 
                to="/labels" 
                className={`text-gray-700 hover:text-gray-900 transition-colors font-bold ${
                  location.pathname === '/labels' ? 'text-gray-900 underline underline-offset-4' : 'text-gray-600'
                }`}
              >
                Labels
              </Link>
              <Link 
                to="/explore" 
                className={`text-gray-700 hover:text-gray-900 transition-colors font-bold ${
                  location.pathname === '/explore' ? 'text-gray-900 underline underline-offset-4' : 'text-gray-600'
                }`}
              >
                Explore
              </Link>
            </div>
          )}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button 
                  onClick={toggleUserMenu}
                  className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <span className="hidden sm:inline">{user?.email}</span>
                  <svg 
                    className="h-4 w-4" 
                    fill="none" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path d={isUserMenuOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                  </svg>
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <Link 
                      to="/change-password" 
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Change Password
                    </Link>
                    <button
                      onClick={() => {
                        signOut();
                        setIsUserMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              location.pathname !== '/login' && (
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Sign In
                </Link>
              )
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isAuthenticated && (
        <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} bg-green-300`}>
          <div className="px-4 pt-2 pb-3 space-y-1">
            <Link 
              to="/" 
              className={`block px-3 py-2 rounded-md text-base font-bold text-gray-700 hover:text-gray-900 hover:bg-green-200 ${
                location.pathname === '/' ? 'bg-green-200 text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              New
            </Link>
            <Link 
              to="/collection" 
              className={`block px-3 py-2 rounded-md text-base font-bold text-gray-700 hover:text-gray-900 hover:bg-green-200 ${
                location.pathname === '/collection' ? 'bg-green-200 text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Collection
            </Link>
            <Link 
              to="/labels" 
              className={`block px-3 py-2 rounded-md text-base font-bold text-gray-700 hover:text-gray-900 hover:bg-green-200 ${
                location.pathname === '/labels' ? 'bg-green-200 text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Labels
            </Link>
            <Link 
              to="/explore" 
              className={`block px-3 py-2 rounded-md text-base font-bold text-gray-700 hover:text-gray-900 hover:bg-green-200 ${
                location.pathname === '/explore' ? 'bg-green-200 text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Explore
            </Link>
            <div className="border-t border-green-400 pt-2">
              <div className="px-3 py-2 text-sm text-gray-700">{user?.email}</div>
              <Link 
                to="/change-password" 
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-green-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Change Password
              </Link>
              <button
                onClick={() => {
                  signOut();
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-green-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
});

export default Navbar; 